// src/context/CoinleyContext.jsx - Updated with wallet-connect-core integration
import React, { createContext, useContext, useState, useEffect } from 'react';
import { initializeApi, getMerchantWallets } from '../services/api';
import { 
  CoinleyWalletProvider, 
  WalletModal,
  initializeWalletCore 
} from '../services/walletCoreService';

// Import shared styles
import '../styles/theme.css';

const CoinleyContext = createContext();

export const useCoinley = () => useContext(CoinleyContext);

export const CoinleyProvider = ({
  apiKey,
  apiSecret,
  apiUrl = 'https://coinleyserver-production.up.railway.app',
  merchantWalletAddress = null, // Legacy prop for backward compatibility
  merchantSolWalletAddress = null, // Legacy prop for backward compatibility
  debug = false,
  // New wallet core configuration options
  walletConfig = {},
  projectId = null, // WalletConnect project ID (optional)
  enableWalletModal = true,
  children
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [merchantWallets, setMerchantWallets] = useState({});
  const [walletCoreConfig, setWalletCoreConfig] = useState(null);

  useEffect(() => {
    if (!apiKey || !apiSecret) {
      setError('API key and secret are required');
      return;
    }

    const initializeSDK = async () => {
      try {
        // Initialize the API service
        await initializeApi({
          apiKey,
          apiSecret,
          apiUrl,
          debug
        });

        // Initialize wallet core configuration
        const coreConfig = initializeWalletCore({
          appName: 'Coinley Checkout',
          appDescription: 'Secure cryptocurrency payment processing',
          projectId, // WalletConnect project ID (optional)
          theme: 'auto',
          enableAnalytics: false,
          ...walletConfig
        });
        
        setWalletCoreConfig(coreConfig);

        // Fetch merchant wallet addresses
        const wallets = await getMerchantWallets();
        
        // Merge with legacy props if provided
        const allWallets = {
          ...wallets,
          ...(merchantWalletAddress && { ethereum: merchantWalletAddress }),
          ...(merchantSolWalletAddress && { solana: merchantSolWalletAddress })
        };

        setMerchantWallets(allWallets);
        setIsInitialized(true);

      } catch (err) {
        setError(err.message);
        if (debug) {
          console.error('Coinley SDK initialization error:', err);
        }
      }
    };

    initializeSDK();
  }, [apiKey, apiSecret, apiUrl, merchantWalletAddress, merchantSolWalletAddress, debug, walletConfig, projectId]);

  // Method to refresh merchant wallets
  const refreshMerchantWallets = async () => {
    try {
      const wallets = await getMerchantWallets();
      setMerchantWallets(wallets);
      return wallets;
    } catch (err) {
      console.error('Failed to refresh merchant wallets:', err);
      return merchantWallets;
    }
  };

  // Method to store payment data from responses for easy context access
  const storePaymentData = (data) => {
    setPaymentData(data);
    return data;
  };

  const value = {
    apiKey,
    apiSecret,
    apiUrl,
    merchantWalletAddress, // Legacy
    merchantSolWalletAddress, // Legacy
    merchantWallets, // New: All merchant wallets
    isInitialized,
    error,
    debug,
    paymentData,
    storePaymentData,
    refreshMerchantWallets,
    // New wallet core properties
    walletCoreConfig,
    projectId,
    enableWalletModal
  };

  if (!walletCoreConfig) {
    return null
  }

  return (
    <CoinleyContext.Provider value={value}>
      <CoinleyWalletProvider 
        config={walletCoreConfig}
        walletConfig={{
          appName: 'Coinley Checkout',
          theme: walletConfig.theme || 'auto'
        }}
      >
        {children}
        {enableWalletModal && <WalletModal />}
      </CoinleyWalletProvider>
    </CoinleyContext.Provider>
  );
};